<template>
  <div class="tax-invoice-panel" :class="{ 'is-compact': compact }">
    <label v-if="showToggle" class="tax-invoice-panel__check">
      <input
        type="checkbox"
        :checked="enabled"
        @change="onToggle($event)"
      />
      <span>세금계산서 발행 요청</span>
    </label>

    <p v-if="showToggle" class="tax-invoice-panel__later">
      지금 요청하지 않아도, 입금 확인 후 주문상세에서 요청할 수 있습니다.
    </p>

    <template v-if="active">
      <div class="tax-invoice-panel__notice">
        <AppIcon icon="mdi-file-document-outline" :size="18" />
        <p>
          세금계산서 발행을 위해 <strong>사업자등록증</strong>과 <strong>수신 이메일</strong>이
          필요합니다. <strong>마이페이지로 가지 않아도</strong> 아래에서 바로 확인·등록할 수 있습니다.
        </p>
      </div>

      <ul class="tax-invoice-panel__ready" aria-label="세금계산서 발행 준비">
        <li :class="{ 'is-ok': Boolean(bizCertUrl) }">
          <AppIcon :icon="bizCertUrl ? 'mdi-check-circle' : 'mdi-alert-circle-outline'" :size="16" />
          사업자등록증 {{ bizCertUrl ? '등록됨' : '업로드 필요' }}
        </li>
        <li :class="{ 'is-ok': emailReady }">
          <AppIcon :icon="emailReady ? 'mdi-check-circle' : 'mdi-alert-circle-outline'" :size="16" />
          수신 이메일 {{ emailReady ? '입력됨' : '입력 필요' }}
        </li>
      </ul>

      <div class="tax-invoice-panel__fields">
        <label>
          <span>상호</span>
          <input
            :value="bizName"
            type="text"
            maxlength="100"
            placeholder="상호"
            @input="emit('update:bizName', ($event.target as HTMLInputElement).value.trim())"
          />
        </label>
        <label>
          <span>사업자번호</span>
          <input
            :value="bizNum"
            maxlength="12"
            inputmode="numeric"
            placeholder="000-00-00000"
            @input="emit('update:bizNum', formatBizNumberInput(($event.target as HTMLInputElement).value))"
          />
        </label>

        <div class="tax-invoice-panel__cert">
          <span>사업자등록증</span>
          <label class="tax-invoice-panel__upload">
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf"
              :disabled="bizCertUploading"
              @change="onBizCertChange"
            />
            <span>{{ bizCertUrl ? '다시 업로드' : '파일 선택' }}</span>
          </label>
          <p v-if="bizCertUploading" class="tax-invoice-panel__msg tax-invoice-panel__msg--muted">
            업로드 중…
          </p>
          <p v-else-if="bizCertUrl" class="tax-invoice-panel__msg tax-invoice-panel__msg--ok">
            등록증이 등록되어 있습니다.
            <a :href="bizCertHref" target="_blank" rel="noopener" class="tax-invoice-panel__link">
              미리보기
            </a>
          </p>
          <p v-else class="tax-invoice-panel__msg tax-invoice-panel__msg--warn">
            사업자등록증을 업로드해 주세요. (jpg · png · webp · pdf, 최대 10MB)
          </p>
        </div>

        <label class="is-full">
          <span>세금계산서 수신 이메일</span>
          <input
            :value="email"
            type="email"
            maxlength="100"
            placeholder="example@company.com"
            @input="emit('update:email', ($event.target as HTMLInputElement).value.trim())"
          />
          <em class="tax-invoice-panel__hint">발행된 세금계산서를 이 주소로 보내드립니다.</em>
        </label>

        <label class="is-full tax-invoice-panel__confirm">
          <input v-model="confirmed" type="checkbox" />
          <span>
            사업자등록증과 이메일(<strong>{{ email || '미입력' }}</strong>)을 확인했습니다.
            세금계산서를 위 주소로 받겠습니다.
          </span>
        </label>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { saveMemberBizCert, uploadMemberBizCert, updateMemberContact } from '@/api/member'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { formatBizNumberInput, isEmailValid } from '@/utils/formValidation'

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')

const props = withDefaults(
  defineProps<{
    enabled?: boolean
    bizName: string
    bizNum: string
    email: string
    showToggle?: boolean
    compact?: boolean
  }>(),
  {
    enabled: true,
    showToggle: true,
    compact: false,
  },
)

const emit = defineEmits<{
  'update:enabled': [value: boolean]
  'update:bizName': [value: string]
  'update:bizNum': [value: string]
  'update:email': [value: string]
}>()

const { member, setMember } = useMemberAuth()

const bizCertUrl = ref('')
const bizCertUploading = ref(false)
const confirmed = ref(false)
const pendingBizCertUrl = ref('')

const active = computed(() => (props.showToggle ? props.enabled : true))

const emailReady = computed(() => isEmailValid(props.email))

const bizCertHref = computed(() =>
  bizCertUrl.value ? `${API_ORIGIN}${bizCertUrl.value}` : '',
)

function syncFromMember() {
  const m = member.value
  if (!m || !active.value) return

  if (!bizCertUrl.value && !pendingBizCertUrl.value) {
    bizCertUrl.value = m.bizCert || ''
  }
  if (!props.email.trim() && m.bossEmail) {
    emit('update:email', m.bossEmail)
  }
  if (!props.bizName.trim() && m.orgName) {
    emit('update:bizName', m.orgName)
  }
  if (!props.bizNum.trim() && m.bizNum) {
    emit('update:bizNum', formatBizNumberInput(m.bizNum))
  }
}

watch(
  () => member.value?.bizCert,
  (value) => {
    if (value && !pendingBizCertUrl.value) {
      bizCertUrl.value = value
    }
  },
  { immediate: true },
)

watch(
  active,
  (value) => {
    if (value) syncFromMember()
    else confirmed.value = false
  },
  { immediate: true },
)

onMounted(() => {
  syncFromMember()
})

function onToggle(event: Event) {
  const checked = (event.target as HTMLInputElement).checked
  emit('update:enabled', checked)
  if (checked) syncFromMember()
}

async function onBizCertChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  bizCertUploading.value = true
  try {
    const uploaded = await uploadMemberBizCert(file)
    pendingBizCertUrl.value = uploaded.url
    bizCertUrl.value = uploaded.url
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '사업자등록증 업로드에 실패했습니다.')
  } finally {
    bizCertUploading.value = false
    input.value = ''
  }
}

function validate(): string | null {
  if (!active.value) return null

  if (!props.bizName.trim()) {
    return '세금계산서 상호를 입력해 주세요.'
  }
  if (props.bizNum.replace(/\D/g, '').length < 10) {
    return '세금계산서 사업자번호를 입력해 주세요.'
  }
  if (!bizCertUrl.value) {
    return '세금계산서 발행을 위해 사업자등록증을 업로드해 주세요.'
  }
  if (!props.email.trim()) {
    return '세금계산서를 받을 이메일을 입력해 주세요.'
  }
  if (!isEmailValid(props.email)) {
    return '이메일 형식을 확인해 주세요.'
  }
  if (!confirmed.value) {
    return '사업자등록증과 이메일 확인에 동의해 주세요.'
  }
  return null
}

async function persist(): Promise<void> {
  if (!active.value) return

  if (pendingBizCertUrl.value) {
    const updated = await saveMemberBizCert(pendingBizCertUrl.value)
    pendingBizCertUrl.value = ''
    if (updated) setMember(updated)
  }

  const updated = await updateMemberContact({ email: props.email.trim() })
  if (updated) setMember(updated)
}

function confirmMessage(): string {
  return `세금계산서를 ${props.email.trim()}로 보내드립니다.\n사업자등록증과 이메일이 맞는지 다시 확인해 주세요.`
}

defineExpose({
  validate,
  persist,
  confirmMessage,
  hasBizCert: () => Boolean(bizCertUrl.value),
  hasEmail: () => emailReady.value,
})
</script>

<style lang="scss" src="./TaxInvoiceRequestPanel.scss"></style>
